import {fromPromise} from "rxjs/internal-compatibility";
import {reduce} from "rxjs/operators";
import TrackingService from 'app/services/tracking/tracking.service';
import { Observable } from 'rxjs';
import CustomerUpdateService from "external-services/customer-update-service/customerUpdate.service";
import { IServiceItem, IEvent, IServiceInterval, ICustomer, ServiceEventType } from 'shared-types/index';
import DateUtilsService from 'external-services/date-utils-service';
import AdminService from 'external-services/diary-admin-service/admin.service';
import PouchLoadService from "external-services/pouch-load-service";
import EventsLookupService from "external-services/events-lookup-service";
import GridSyncService from "app/services/gridSync/gridSync.service";

const NS = 'EventsService';
class EventsService {
  public async getEventsOnDate(date: Date): Promise<IEvent[]> {
    const events: IEvent[] = await EventsLookupService.getAll();
    const eventsOnDate: IEvent[] = [];
    events.forEach((eventObj) => {
      if (DateUtilsService.isOnSameDate(eventObj.date as Date, date)) {
        // the blackout occuers on this date
        eventsOnDate.push(eventObj);
      }
    })
    return eventsOnDate;
  }

  public async getEventsFromDate(date: Date = undefined): Promise<IEvent[]> {
    const timezoneId = AdminService.getVenueTimezoneId();
    date = DateUtilsService.getClearDate(date, timezoneId);
    const events = await EventsLookupService.getAll();
    const eventsFromDate: IEvent[] = [];
    events.forEach((eventObj) => {
      console.log(NS, 'eventObj.date', eventObj.date)
      if (DateUtilsService.getClearDate(eventObj.date as Date, timezoneId).getTime() >= date.getTime()) {
        eventsFromDate.push(eventObj);
      }
    })
    return eventsFromDate;
  }

  public async getEventsBetweenDates(startDate: Date, endDate: Date): Promise<IEvent[]> {
    const timezoneId = AdminService.getVenueTimezoneId();
    startDate = DateUtilsService.getClearDate(startDate, timezoneId);
    endDate = DateUtilsService.getClearDate(endDate, timezoneId);

    const events = await EventsLookupService.getAll();
    const eventsFromDate: IEvent[] = [];
    events.forEach((eventObj) => {
      if (DateUtilsService.getClearDate(eventObj.date as Date, timezoneId).getTime() >= startDate.getTime()
        && DateUtilsService.getClearDate(eventObj.date as Date, timezoneId).getTime() <= endDate.getTime()) {
        eventsFromDate.push(eventObj);
      }
    })
    return eventsFromDate;
  }

  public async save(eventObj: IEvent, customer: ICustomer): Promise<void> {
    const isNewEvent = !eventObj._id;
    if (!eventObj._id) {
      // is a new block out add an ID
      eventObj._id = PouchLoadService.getNewId(['event']);
      TrackingService.trackEvent('New Event', {
        id: eventObj._id
      });
    } else {
      TrackingService.trackEvent('Edit Event', {
        id: eventObj._id,
        rev: eventObj._rev
      });
    }

    if (eventObj.paymentDetails && eventObj.paymentDetails.discountAmount === null) {
      delete eventObj.paymentDetails.discountAmount;
    }

    eventObj.type = 'event';

    // convert datetime to strings
    eventObj.date = DateUtilsService.dateToDateTimeStr(eventObj.date as Date);
    // remove unknown when update following function in future
    eventObj.startTime = DateUtilsService.dateToTimeStr(eventObj.startTime, 'HH:mm')as unknown as Date;
    eventObj.firstBookingTime = DateUtilsService.dateToTimeStr(eventObj.firstBookingTime as Date, 'HH:mm')as unknown as Date;
    eventObj.lastBookingTime = DateUtilsService.dateToTimeStr(eventObj.lastBookingTime, 'HH:mm')as unknown as Date;
    eventObj.endTime = DateUtilsService.dateToTimeStr(eventObj.endTime, 'HH:mm')as unknown as Date;
    eventObj.startDateTime = DateUtilsService.dateToDateTimeStr(eventObj.startDateTime as Date);
    eventObj.endDateTime = DateUtilsService.dateToDateTimeStr(eventObj.endDateTime as Date);
    // Add or Update the customer
    if (eventObj.eventType === ServiceEventType.PrivateFunction) {
      if (customer) {
        const storeCustomerPromise: Promise<ICustomer> = customer._id ? CustomerUpdateService.update(customer) : CustomerUpdateService.add(customer);
        customer = await storeCustomerPromise;
      } else if (!eventObj.customerId) {
        // if there is no customer passed and no customer Id is attached, reject
        throw ({
          type: 'customer',
          message: 'No customer attached to this Private Function'
        });
      }
    }


    //Clean up custom interval if enableCustomPaxPerIntervals is false
    //https://nowbookit.atlassian.net/browse/NBI-4827
    if (!eventObj.enableCustomPaxPerIntervals && eventObj.intervalsCustom) {
       eventObj.intervalsCustom.forEach((interval:IServiceInterval) => {
          interval.values = "";
       });
    }

    try {
      this.updateEventCustomerInfo(eventObj, customer);
      await PouchLoadService.getDbLocal().put(eventObj)
      EventsLookupService.clearCache();
      // CLI-165: notify the Grid Sync API of the change
      if (isNewEvent) {
        GridSyncService.eventCreated(eventObj._id);
      } else {
        GridSyncService.eventEdited(eventObj._id);
      }
      return;
    } catch (e) {
      throw e;
    }

  }

  public async deleteEvent(eventObj: IEvent): Promise<any> {
    TrackingService.trackEvent('Delete Event', {
      id: eventObj._id,
      rev: eventObj._rev
    });

    EventsLookupService.clearCache();
    const deletedEventId = eventObj._id;
    return PouchLoadService.getDbLocal().remove(eventObj as unknown as PouchDB.Core.RemoveDocument)
      .then((response) => {
        // CLI-165: notify the Grid Sync API of the deletion
        GridSyncService.eventDeleted(deletedEventId);
        return response;
      })
  }

  private updateEventCustomerInfo(event: IEvent, customer: ICustomer): boolean {
    let updated = false;

    if (customer && event) {
      if (event.customerId !== customer._id) {
        event.customerId = customer._id;
        updated = true;
      }

      if (event.firstName !== customer.firstName) {
        event.firstName = customer.firstName;
        updated = true;
      }

      if (event.lastName !== customer.lastName) {
        event.lastName = customer.lastName;
        updated = true;
      }

      if (event.company !== customer.company) {
        event.company = customer.company;
        updated = true;
      }

      if (event.email !== customer.email) {
        event.email = customer.email;
        updated = true;
      }

      if (event.phone !== customer.phone) {
        event.phone = customer.phone;
        updated = true;
      }
    }

    return updated;
  }


  /**
   * Returns booking/menu options that are still active in the schedule.
   * Note: there is a similar function in `scheduleService` called `getActiveMenuOptions`.
   */
  public getActiveMenuOptions(): Observable<string[]> {
    return fromPromise(
      this.getEventsFromDate(new Date())
    )
      .pipe(
        reduce((acc: string[], services: IEvent[]) => {
          services.forEach(service => {
            if (service.paymentDetails && service.paymentDetails.menuOptions) {
              // extract menuOptions ids
              acc = acc.concat(service.paymentDetails.menuOptions);
              // filter out duplicate
              acc = acc.filter((menuOptionId, i) => acc.indexOf(menuOptionId) === i)
            }
          });
          return acc;
        }, [])
      );
  }
// move from 'src/app/bookings/bookingsController/bookings.controller'
  public isPrivateFunEvent(service:IServiceItem):boolean{
    return service && service.type === 'event' && service.eventType === 'PrivateFunction';
  }

}
const instance = new EventsService();
export default instance as EventsService;
